import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { KeyService } from '@core/services/key.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';

@Component({
    selector: 'rh-create-key',
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatGridListModule,
        MatInputModule,
        NgxMatTimepickerModule,
        ReactiveFormsModule,
    ],
    providers: [
        provideNativeDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    ],
    templateUrl: './create-key.component.html',
    styleUrl: './create-key.component.scss'
})
export class CreateKeyComponent implements OnInit, OnDestroy {
  private readonly keyService = inject(KeyService);

  readonly date = new FormControl<string | null>(null);
  readonly dateTime = new FormControl<string>('');
  readonly expires = new FormControl<boolean>(false);
  readonly keyName = new FormControl<string>('', [Validators.required]);
  readonly time = new FormControl<string | null>('12:59 PM');
  private readonly onDestroy = new Subject();

  ngOnInit(): void {
    this.expires.valueChanges
      .pipe(takeUntil(this.onDestroy), distinctUntilChanged())
      .subscribe((val) => {
        if (val !== null && val) {
          const expireDate = new Date(
            new Date().setFullYear(new Date().getFullYear() + 1)
          ).toISOString();
          this.date.setValue(expireDate);
        } else {
          this.date.setValue(null);
        }
      });

    this.time.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.combineDateTime());
    this.date.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.combineDateTime());
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  public createKey() {
    if (this.keyName.valid) {
      this.keyService.createKey({
        keyName: this.keyName.value ?? '',
        expires: this.dateTime.value ?? null,
      });
    }
  }

  private combineDateTime(): void {
    const dateValue = this.date.value;
    const timeValue = this.time.value;

    if (dateValue && timeValue) {
      const date = new Date(dateValue);
      const [time, modifier] = timeValue.split(' ');
      let [hours, minutes] = time.split(':').map(Number);

      if (modifier === 'PM' && hours < 12) {
        hours += 12;
      } else if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }

      date.setHours(hours, minutes, 0, 0);
      const combinedDateTimeISO = date.toISOString();
      this.dateTime.patchValue(combinedDateTimeISO);

      const combinedDateTimeFormatted = date.toLocaleString();
      console.info('Combined DateTime (ISO):', combinedDateTimeISO);
      console.info('Combined DateTime (Formatted):', combinedDateTimeFormatted);
    }
  }
}
